/**
 * useWorkspaceCredentials — sl-vgk8.2
 *
 * Hook for managing workspace credentials via REST API.
 * Pattern matches use-workspace-ai-settings.ts.
 */
import { notify } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import type { ConnectionDefinition } from '@affine/core/modules/streamlain/connections/types';
import type { NodeTypeDefinition } from '@affine/core/modules/streamlain/workflow-viewer/types/node-type-definition';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

// =============================================================================
// Types
// =============================================================================

export type CredentialType = 'api_key' | 'oauth2' | 'basic_auth';
export type CredentialStatus = 'active' | 'expired' | 'error';

export interface WorkspaceCredentialInfo {
  id: string;
  workspaceId: string;
  name: string;
  type: CredentialType;
  service: string;
  status: CredentialStatus;
  canRevealSecret: boolean;
  effectiveRole?: 'Owner' | 'Admin' | 'Editor' | 'Reader' | null;
  maskedHint: string;
  lastUsedAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface ApiKeyData {
  key: string;
  headerName?: string;
  headerPrefix?: string;
}

export interface BasicAuthData {
  username: string;
  password: string;
}

export interface OAuth2Data {
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  scopes?: string[];
}

export type CredentialData = ApiKeyData | BasicAuthData | OAuth2Data;

/** sl-bmpq.8.1: Service definition from GET /api/service-definitions */
export interface ServiceDefinitionInfo {
  id: string;
  slug: string;
  name: string;
  icon: string;
  category: string;
  authType: string;
  oauthConfig: {
    authorize_url: string;
    token_url: string;
    default_scopes: string[];
    extra_params: Record<string, unknown>;
  } | null;
  capabilities: string[];
  byoaSetupGuide: string | null;
  connectionDefinition: ConnectionDefinition;
  nodeTypes: readonly ServiceNodeTypeInfo[];
}

/** A step of a service as GET /api/service-definitions lists it. */
export type ServiceNodeTypeInfo = Pick<
  NodeTypeDefinition,
  'slug' | 'name' | 'titlesI18n'
> & {
  readonly showInPicker: boolean;
  readonly lifecycleStatus: 'experimental' | 'beta' | 'stable' | 'deprecated';
};

/** sl-bmpq.7.2: Info about a workflow using a credential */
export interface CredentialUsageInfo {
  workflowId: string;
  workflowName: string;
  stepName: string;
  stepType: string;
}

/** sl-omnc: Metadata-only credential entry for admin overview. */
export interface CredentialAdminOverviewEntry {
  id: string;
  name: string;
  type: CredentialType;
  service: string;
  status: CredentialStatus;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
}

// =============================================================================
// Hook
// =============================================================================

const failureReason = (error: unknown) =>
  error instanceof Error ? error.message : undefined;

export function useWorkspaceCredentials() {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const backendAvailable = useBackendAvailable();
  const workspaceId = workspaceService.workspace.id;

  // sl-oe5x: SWR-backed fetch deduplicates the 2× /credentials fan-out
  // the AI panel used to cause (credential-slot + banner both hitting the
  // endpoint in parallel). One request per workspaceId, shared cache.
  const credentialsUrl =
    backendAvailable && workspaceId
      ? `/api/workspace/${workspaceId}/credentials`
      : null;
  const { data, error, mutate } = useSWRImmutable<{
    credentials: WorkspaceCredentialInfo[];
  }>(
    credentialsUrl,
    async (url: string) =>
      defaultWorkspaceApiClient.requestJson<{
        credentials: WorkspaceCredentialInfo[];
      }>({
        workspaceId,
        path: url,
        category: 'settings',
        key: 'workspace-credentials:list',
        mode: 'background',
        priority: 'visible',
      }),
    { suspense: false }
  );
  if (error) {
    if (isBackendUnavailableError(error)) {
      markBackendUnavailable(error);
    }
    console.error('[sl-vgk8.2] Failed to load credentials:', error);
  }
  const credentials = useMemo(
    () => data?.credentials ?? [],
    [data?.credentials]
  );
  const isLoading = !!credentialsUrl && !data && !error;
  const [isSaving, setIsSaving] = useState(false);

  const fetchCredentials = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const createCredential = useCallback(
    async (
      name: string,
      type: CredentialType,
      service: string,
      data: CredentialData
    ): Promise<string | undefined> => {
      setIsSaving(true);
      try {
        const body = await defaultWorkspaceApiClient.requestJson<{
          credential?: { id: string };
        }>({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, type, service, data }),
          },
          category: 'settings',
          key: `workspace-credentials:create:${name}`,
          mode: 'foreground',
          priority: 'foreground',
        });
        notify.success({
          title: t[
            'com.streamlain.settings.workspace.credentials.toast.created'
          ]({ name }),
        });
        await fetchCredentials();
        return body.credential?.id;
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.credentials.toast.create-failed'
            ](),
          message: failureReason(err),
        });
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [workspaceId, fetchCredentials, t]
  );

  const updateCredential = useCallback(
    async (credentialId: string, data: CredentialData) => {
      setIsSaving(true);
      try {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}`,
          init: {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data }),
          },
          category: 'settings',
          key: `workspace-credentials:update:${credentialId}`,
          mode: 'foreground',
          priority: 'foreground',
        });
        notify.success({
          title:
            t['com.streamlain.settings.workspace.credentials.toast.updated'](),
        });
        await fetchCredentials();
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.credentials.toast.update-failed'
            ](),
          message: failureReason(err),
        });
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [workspaceId, fetchCredentials, t]
  );

  const deleteCredential = useCallback(
    async (
      credentialId: string,
      name: string,
      force = false
    ): Promise<{ deleted: boolean; usages?: CredentialUsageInfo[] }> => {
      setIsSaving(true);
      try {
        const url = `/api/workspace/${workspaceId}/credentials/${credentialId}${force ? '?force=true' : ''}`;
        const res = await defaultWorkspaceApiClient.request({
          workspaceId,
          path: url,
          init: { method: 'DELETE' },
          category: 'settings',
          key: `workspace-credentials:delete:${credentialId}:${force ? 'force' : 'safe'}`,
          mode: 'foreground',
          priority: 'foreground',
        });

        // 409 Conflict — credential is used by workflows
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          return { deleted: false, usages: body.usages ?? [] };
        }

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message ?? `HTTP ${res.status}`);
        }
        notify.success({
          title: t[
            'com.streamlain.settings.workspace.credentials.toast.deleted'
          ]({ name }),
        });
        await fetchCredentials();
        return { deleted: true };
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.credentials.toast.delete-failed'
            ](),
          message: failureReason(err),
        });
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [workspaceId, fetchCredentials, t]
  );

  // sl-bmpq.7.2: Fetch credential usage (which workflows use this credential).
  // Rejects when the server does not answer; callers name it as unknown.
  const getCredentialUsage = useCallback(
    async (credentialId: string): Promise<CredentialUsageInfo[]> => {
      const data = await defaultWorkspaceApiClient.requestJson<{
        usages?: CredentialUsageInfo[];
      }>({
        workspaceId,
        path: `/api/workspace/${workspaceId}/credentials/${credentialId}/usage`,
        category: 'settings',
        key: `workspace-credentials:usage:${credentialId}`,
        mode: 'background',
        priority: 'visible',
      });
      return data.usages ?? [];
    },
    [workspaceId]
  );

  const revealCredential = useCallback(
    async (credentialId: string): Promise<CredentialData> => {
      const body = await defaultWorkspaceApiClient.requestJson<{
        credential?: { data?: CredentialData };
      }>({
        workspaceId,
        path: `/api/workspace/${workspaceId}/credentials/${credentialId}/reveal`,
        category: 'settings',
        key: `workspace-credentials:reveal:${credentialId}`,
        mode: 'foreground',
        priority: 'foreground',
      });
      return body.credential?.data as CredentialData;
    },
    [workspaceId]
  );

  // sl-bmpq.2.3: OAuth2 Connect Flow — opens popup, handles postMessage callback
  const startOAuthConnect = useCallback(
    (credentialId: string, serviceSlug: string): Promise<boolean> => {
      return new Promise(resolve => {
        const url = `/api/oauth/authorize?workspaceId=${encodeURIComponent(workspaceId)}&serviceSlug=${encodeURIComponent(serviceSlug)}&credentialId=${encodeURIComponent(credentialId)}`;
        const popup = window.open(url, 'oauth-connect', 'width=600,height=700');
        if (!popup) {
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.credentials.toast.popup-blocked'
              ](),
          });
          resolve(false);
          return;
        }

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          const data = event.data as {
            type?: string;
            success?: boolean;
            error?: string;
          };
          if (data?.type !== 'oauth2-callback') return;

          window.removeEventListener('message', handleMessage);
          popup.close();

          if (data.success) {
            notify.success({
              title:
                t[
                  'com.streamlain.settings.workspace.credentials.toast.oauth-connected'
                ](),
            });
            void fetchCredentials();
            resolve(true);
          } else {
            notify.error({
              title:
                t[
                  'com.streamlain.settings.workspace.credentials.toast.oauth-failed'
                ](),
              message: data.error,
            });
            resolve(false);
          }
        };

        window.addEventListener('message', handleMessage);

        // Cleanup if popup is closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            resolve(false);
          }
        }, 500);
      });
    },
    [workspaceId, fetchCredentials, t]
  );

  const transferOwnership = useCallback(
    async (credentialId: string, newOwnerId: string) => {
      setIsSaving(true);
      try {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}/transfer-ownership`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newOwnerId }),
          },
          category: 'settings',
          key: `workspace-credentials:transfer:${credentialId}`,
          mode: 'foreground',
          priority: 'foreground',
        });
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.credentials.toast.transferred'
            ](),
        });
        await fetchCredentials();
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.credentials.toast.transfer-failed'
            ](),
          message: failureReason(err),
        });
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [workspaceId, fetchCredentials, t]
  );

  const listAdminOverview = useCallback(
    async (signal?: AbortSignal): Promise<CredentialAdminOverviewEntry[]> => {
      const data = await defaultWorkspaceApiClient.requestJson<{
        credentials?: CredentialAdminOverviewEntry[];
      }>({
        workspaceId,
        path: `/api/workspace/${workspaceId}/credentials/admin-overview`,
        init: { signal },
        category: 'settings',
        key: 'workspace-credentials:admin-overview',
        mode: 'background',
        priority: 'visible',
      });
      return (data.credentials ?? []) as CredentialAdminOverviewEntry[];
    },
    [workspaceId]
  );

  return {
    credentials,
    isBackendAvailable: backendAvailable,
    isLoading,
    isSaving,
    createCredential,
    updateCredential,
    deleteCredential,
    getCredentialUsage,
    revealCredential,
    startOAuthConnect,
    transferOwnership,
    listAdminOverview,
    refresh: fetchCredentials,
  };
}

// =============================================================================
// sl-bmpq.8.1: Service Definitions Hook (cached)
// =============================================================================

let serviceDefCache: ServiceDefinitionInfo[] | null = null;
let serviceDefPromise: Promise<ServiceDefinitionInfo[]> | null = null;

/** Switched off (`enabled` false), it sends no request, e.g. in a local workspace. */
export function useServiceDefinitions(enabled = true): {
  services: ServiceDefinitionInfo[];
  isLoading: boolean;
  /** The services could not be read; the next mount asks again. */
  failed: boolean;
} {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const [services, setServices] = useState<ServiceDefinitionInfo[]>(
    serviceDefCache ?? []
  );
  const [isLoading, setIsLoading] = useState(!serviceDefCache);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (serviceDefCache) {
      setServices(serviceDefCache);
      setIsLoading(false);
      setFailed(false);
      return;
    }

    if (!serviceDefPromise) {
      serviceDefPromise = defaultWorkspaceApiClient
        .request({
          workspaceId,
          path: '/api/service-definitions',
          category: 'settings',
          key: 'GET:/api/service-definitions',
          mode: 'background',
          priority: 'visible',
        })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<ServiceDefinitionInfo[]>;
        })
        .finally(() => {
          serviceDefPromise = null;
        });
    }

    let cancelled = false;
    serviceDefPromise.then(
      defs => {
        serviceDefCache = defs;
        if (cancelled) return;
        setServices(defs);
        setFailed(false);
        setIsLoading(false);
      },
      (err: unknown) => {
        console.error('[sl-bmpq.8.1] Failed to load service definitions:', err);
        if (cancelled) return;
        setFailed(true);
        setIsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId]);

  if (!enabled) return { services: [], isLoading: false, failed: false };
  return { services, isLoading, failed };
}
